import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { logEvent as logAnalyticsEvent } from '../analytics';
import {
    isFaqSurveyOptionEnabled,
    isMobile,
    isUnified,
    openUrlForWebOrMobile,
} from '../helpers';
import { useBrand, useConfig } from '../hooks';
import { useOptionalInformationalAlert } from '../contextProviders';
import LaunchIcon from '@mui/icons-material/Launch';
import { Activity, AmplitudeEvent, FeedbackEntity } from '../types';
import styles from './Button.module.css';
import sharedStyles from '../shared.module.css';
import { IEventTelemetry } from '@microsoft/applicationinsights-common';
import { getConversationId, logEvent, logException } from '../logging';
import { AmpFlagSet } from '../amplitude';
import {
    isFlowSurveyable,
    isIntentSurveyable,
    postMessageAssistMessage,
    showSurveyOption,
} from '../messageassist';
import { MESSAGE_SUBJECT, MESSAGE_LINK } from '../constants';
import { USER_BUTTON_CLICK } from '../constants';
import { MESSAGE_ASSIST_MESSAGE_EVENT_TYPES } from '../constants';

export type OverrideStyle = 'disable' | '';

export type ButtonData = {
    // Override default button html to use an anchor element.
    renderAsAnchor?: boolean;
    amplitudeEvent?: AmplitudeEvent;
    insightsEvent?: IEventTelemetry;
    data?: string;
    feedback?: boolean;
    feedbackData?: FeedbackEntity;
    title: string;
    url?: string;
    newTab?: boolean;
    action?: string;
    showLaunchIcon?: boolean;
    showPunchOutIcon?: boolean;
    closeChatbot?: boolean;
    disabled?: boolean;
    intent?: string;
    flow?: string;
    showSurvey?: boolean;
    returnToMenu?: boolean;
    ctaType?: string;
    disableAfterClick?: boolean;
};

export type ButtonInfo = {
    button: ButtonData;
    className?: string;
    disableButtons: boolean;
    disableChildMargin?: boolean;
    featureFlags: AmpFlagSet;
    index?: number;
    isCardButton?: boolean;
    onClick?: (e?: any) => void;
    overrideStyle?: OverrideStyle;
    overrideSendMessage?: () => void;
    appendMessage?: (message: Activity) => void;
    sendMessage?: (message: string) => void;
    sendPostBack?: (value: any) => void;
    selected?: boolean;
    setButtonIndexSelected?: React.Dispatch<React.SetStateAction<number>>;
    testId?: string;
    subject?: string;
    autoFocus?: boolean;
};

export const ButtonLabel: React.FC<{
    label: string;
    showRedirectIcon?: boolean;
}> = ({ label, showRedirectIcon }) =>
    showRedirectIcon ? (
        <div className={styles.customButtonLabel}>
            <p>{label}</p>
            <span className={styles.redirectIcon}></span>
        </div>
    ) : (
        <div className={styles.customButtonLabel}>
            <p>{label}</p>
        </div>
    );

const isMessageAssistButton = (button: ButtonData): boolean => {
    return !!button.action?.startsWith('messageassist://');
};

const isMessageAssistDeepLinkButton = (button: ButtonData): boolean => {
    return !!button.action?.endsWith(
        MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.DEEP_LINK
    );
};

export const Button: React.FC<ButtonInfo> = ({
    button,
    className,
    disableButtons,
    disableChildMargin,
    featureFlags,
    index,
    isCardButton = false,
    overrideStyle,
    overrideSendMessage,
    selected,
    appendMessage,
    sendMessage,
    sendPostBack,
    onClick,
    setButtonIndexSelected,
    testId,
    subject,
    autoFocus = false,
}) => {
    const [disabledAfterClick, setDisabledAfterClick] = useState(false);
    const brand = useBrand();
    const { mychartDomains, tier } = useConfig();
    const informationalAlert = useOptionalInformationalAlert();
    const isMaButton = isMessageAssistButton(button);
    const determineSpecialStyling = (): string | null => {
        if (overrideStyle) {
            if (overrideStyle === 'disable') {
                return sharedStyles.customButtonDisabled;
            }
        }
        if (button.disabled && selected) {
            if (button.ctaType === 'secondary') {
                return sharedStyles.customButtonNonPrimarySelectedAndDisabled;
            } else {
                return sharedStyles.customButtonSelectedAndDisabled;
            }
        }
        if (button.disabled) {
            return sharedStyles.customButtonDisabled;
        }
        if (selected) {
            if (
                button.ctaType === 'secondary' ||
                (isCardButton && index !== 0)
            ) {
                return sharedStyles.customButtonNonPrimarySelected;
            } else {
                return sharedStyles.customButtonSelected;
            }
        }
        if (disableButtons) {
            return sharedStyles.customButtonDisabled;
        }
        return null;
    };

    const determineUniversalCardStyling = (): string | null => {
        if (!isCardButton) return null;
        return index == 0
            ? styles.messageAssistButton
            : `${styles.nonPrimaryButtons} ${styles.nonPrimaryCardButtons}`;
    };

    const showPunchOutIcon = (button: ButtonData): boolean | undefined => {
        return !!button.url || button.showPunchOutIcon; // Adding this additional check to make sure that the existing flows don't break
    };

    const onButtonClick = async (
        e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        // Let a button be clicked multiple times unless otherwise specified
        if (button.disableAfterClick) {
            setDisabledAfterClick(true);
        }
        e.stopPropagation();
        if (setButtonIndexSelected && typeof index === 'number')
            setButtonIndexSelected(index);
        if (button.amplitudeEvent) {
            logAnalyticsEvent(
                button.amplitudeEvent.eventType || 'Button selected',
                Object.assign(
                    { brand, button: title },
                    button.amplitudeEvent.eventProperties || {}
                )
            );
        } else {
            logAnalyticsEvent('Button selected', { button: title });
        }
        if (button.insightsEvent) {
            logEvent(button.insightsEvent);
        }
        logEvent({
            name: 'ButtonClick',
            properties: { button: button.title },
        });

        // clear any scheduled redirects
        // in favor of user's direct action (via btn click)
        informationalAlert?.clearScheduledRedirectNotice();

        if (button.closeChatbot) {
            parent.window.postMessage('closeChatbot', '*');
        }
        if (button.renderAsAnchor) {
            logEvent({
                name: 'GoToUrl',
                properties: {
                    url: button.url,
                    mobile: isMobile(),
                    anchorEl: true,
                },
            });
        } else if (button.url) {
            await openUrlForWebOrMobile(
                button.url,
                featureFlags,
                mychartDomains,
                tier,
                button.newTab,
                button.closeChatbot
            );
        }
        if (button.returnToMenu && sendPostBack) {
            sendPostBack({
                displayText: button.title,
                returnToMenu: true,
            });
        } else if (button.action && isMaButton) {
            // MA doesn't send app insight data from rasa as app insight data is currently client-side only
            // so we are constructing an appInsight event here with the data we have
            const maInsightsEvent = {
                name: 'MessageAssistButtonClick',
                properties: {
                    conversationId: getConversationId(),
                    eventType: USER_BUTTON_CLICK,
                    eventName: button.title,
                },
            };

            logEvent(maInsightsEvent);

            let buttonData = button.data;
            if (subject && button.data) {
                const buttonDataObj = JSON.parse(button.data);
                if (!buttonDataObj[MESSAGE_SUBJECT]) {
                    buttonDataObj[MESSAGE_SUBJECT] = subject;
                    buttonData = JSON.stringify(buttonDataObj);
                }
            }

            if (
                !isUnified() &&
                isMessageAssistDeepLinkButton(button) &&
                buttonData
            ) {
                try {
                    const buttonDataObj = JSON.parse(buttonData);
                    await openUrlForWebOrMobile(
                        buttonDataObj[MESSAGE_LINK],
                        featureFlags,
                        mychartDomains,
                        tier,
                        button.newTab,
                        button.closeChatbot
                    );
                } catch (error: any) {
                    logException(
                        'Message Assist - CTA handle error with openUrlForWebOrMobile',
                        {
                            exception: error,
                        }
                    );
                }
            } else {
                try {
                    await postMessageAssistMessage(
                        button.action,
                        button.title,
                        buttonData
                    );
                } catch (error: any) {
                    logException(
                        'Message Assist - CTA handle error with postMessageAssistMessage',
                        {
                            exception: error,
                        }
                    );
                }
            }

            // Present survey for FAQ CTA button click
            // if button intends to show survey prompt,
            // button is survayable,
            // feature flag is enabled (determined by MA),
            // and user isn't in cooldown (determined by MA)
            const isSurveyable =
                (button.intent && isIntentSurveyable(button.intent)) ||
                (button.flow && isFlowSurveyable(button.flow));
            if (
                isMessageAssistDeepLinkButton(button) &&
                button.showSurvey &&
                isSurveyable &&
                isFaqSurveyOptionEnabled()
            ) {
                await showSurveyOption(appendMessage, sendPostBack);
            }
        } else if (overrideSendMessage) {
            overrideSendMessage();
        } else if (sendPostBack && button.action) {
            sendPostBack({
                text: button.action,
                displayText: button.data || button.title,
            });
        } else if (sendMessage) {
            sendMessage(button.data || button.title);
        }

        if (onClick) {
            onClick(e);
        }
    };

    const { renderAsAnchor, url, title, data, showLaunchIcon } = button;

    const buttonChildMargin = disableChildMargin
        ? null
        : styles.customButtonChildMargins;
    // special case - we put wait time in this button, but that can change, so would break tests sometimes
    const ariaLabel = title.includes('Start your virtual visit now')
        ? 'Start your virtual visit now'
        : title || data;

    const buttonLabel = showLaunchIcon ? (
        <div className={styles.customButtonBotLabelContainer}>
            <ButtonLabel
                label={title}
                showRedirectIcon={showPunchOutIcon(button) || false}
            />
            <LaunchIcon fontSize="small" />
        </div>
    ) : (
        <ButtonLabel
            label={title}
            showRedirectIcon={showPunchOutIcon(button) || false}
        />
    );

    let classNameCTAType = '';

    if (button.ctaType === 'secondary') {
        classNameCTAType = styles.nonPrimaryButtons;
    }

    const fullClassName = classNames(
        className,
        classNameCTAType,
        styles.customButton,
        buttonChildMargin,
        determineSpecialStyling(),
        determineUniversalCardStyling(),
        disabledAfterClick && sharedStyles.customButtonDisabled
    );

    return (
        <>
            {renderAsAnchor ? (
                <a
                    aria-label={ariaLabel}
                    href={url}
                    onClick={onButtonClick}
                    className={fullClassName}
                    target={button.newTab ? '_blank' : '_self'}
                    data-testid={testId || `anchor-button-test-id-${title}`}
                    autoFocus={autoFocus}
                >
                    {buttonLabel}
                </a>
            ) : (
                <button
                    autoFocus={autoFocus}
                    className={fullClassName}
                    aria-label={ariaLabel}
                    onClick={onButtonClick}
                    data-testid={testId || `button-test-id-${title}`}
                    disabled={disabledAfterClick}
                >
                    {buttonLabel}
                </button>
            )}
        </>
    );
};

export const ButtonContainer: React.FC<{
    featureFlags: AmpFlagSet;
    overrideContainerStyle?: boolean;
    isCardButton?: boolean;
    buttons: ButtonInfo[];
    cardSubject?: string;
}> = ({
    featureFlags,
    isCardButton = false,
    overrideContainerStyle,
    buttons,
    cardSubject,
}) => {
    const firstRender = useRef(true);
    const [buttonIndexSelected, setButtonIndexSelected] = useState(-1);
    const shouldHideContainer = buttons.some(
        (b) => b.selected || b.disableButtons
    );
    const hideContainerClass = shouldHideContainer
        ? sharedStyles.containerHidden
        : null;
    const buttonNames: string[] = [];
    buttons.forEach((button) => {
        buttonNames.push(button.button.title);
    });

    useEffect(() => {
        if (!buttons || buttons.length === 0) {
            return;
        }

        // Log AppInsights event on first render of component
        if (firstRender.current) {
            firstRender.current = false;
            logEvent({
                name: 'ButtonsDisplayed',
                properties: { buttons: buttonNames.toString() },
            });
        }
    }, [buttons]);

    return (
        <div
            className={classNames(
                overrideContainerStyle === true
                    ? styles.overrideCustomButtonContainer
                    : null,
                styles.customButtonContainer,
                hideContainerClass
            )}
        >
            {buttons.map((buttonInfo, index) => {
                const messageSubject =
                    cardSubject || buttonInfo.subject || 'Medical question';
                return (
                    <Button
                        autoFocus={buttonInfo.autoFocus}
                        button={buttonInfo.button}
                        className={buttonInfo.className}
                        disableChildMargin={buttonInfo.disableChildMargin}
                        disableButtons={buttonInfo.disableButtons}
                        featureFlags={featureFlags}
                        index={buttonInfo.index}
                        isCardButton={isCardButton}
                        key={`button-${index}`}
                        onClick={buttonInfo.onClick}
                        overrideStyle={buttonInfo.overrideStyle}
                        overrideSendMessage={buttonInfo.overrideSendMessage}
                        selected={buttonIndexSelected === buttonInfo.index}
                        appendMessage={buttonInfo.appendMessage}
                        sendMessage={buttonInfo.sendMessage}
                        sendPostBack={buttonInfo.sendPostBack}
                        setButtonIndexSelected={setButtonIndexSelected}
                        subject={messageSubject}
                    />
                );
            })}
        </div>
    );
};
